import { useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import { ArrowLeft, Mail, Loader2, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '../lib/auth'
import NexaticeIcon from '../assets/nexatice-icon.svg'

export function ForgotPassword() {
  const { forgotPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email) {
      setError('Please enter your email address')
      return
    }

    setIsLoading(true)
    const result = await forgotPassword(email)
    setIsLoading(false)

    if (result.success) {
      setSuccess(true)
    } else {
      setError(result.message)
    }
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="auth" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <a
            href="/login"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </a>

          <div className="auth-card">
            <div className="auth-header">
              <img src={NexaticeIcon} alt="Nexatice" className="auth-logo" />
              <h1>Reset password</h1>
              <p>We'll send you a link to reset your password</p>
            </div>

            {success ? (
              <div className="auth-success">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h2>Check your email</h2>
                <p>
                  We've sent a password reset link to <strong>{email}</strong>. Click the link
                  in the email to reset your password.
                </p>
                <p className="mt-4 text-sm text-zinc-500">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => setSuccess(false)}
                    className="text-emerald-400 hover:text-emerald-300"
                  >
                    try again
                  </button>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                {error && (
                  <div className="auth-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <button type="submit" className="auth-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

